import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { AlertCircle, CheckCircle, Settings, Zap, FileCheck, Calendar, Languages, RefreshCw, Wand2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import storage from '../utils/storage';
import { getBackendUrl } from '../utils/api';

const BACKEND_URL = getBackendUrl();

// Greek labels for the 46 sections — keeps the dropdown human-readable.
// Falls back to the raw key if no label is registered here.
const SECTION_LABELS = {
  morning: '☀️ ΠΡΩΙΝΟ (06:00-10:00)',
  daytime: '🌤️ DAYTIME (10:00-16:00)',
  afternoon: '🌅 ΑΠΟΓΕΥΜΑ / DRIVE TIME (16:00-19:00)',
  evening: '🌙 ΒΡΑΔΥ (19:00-24:00)',
  weekend: '🎉 ΣΑΒΒΑΤΟΚΥΡΙΑΚΟ',
  zodiac: 'Ζώδια',
  sex_relationships: 'Sex & Relationships',
  for_women_only: '💖 Για γυναίκες μόνο',
  weather: 'Καιρός',
  world_days: 'Παγκόσμιες Ημέρες',
  birthdays: 'Γενέθλια Διασήμων',
  weird_news: 'Weird News',
  strange_weird: 'Παράξενα & Weird',
  trends: 'Daily Trends',
  music_lifestyle: 'Music & Lifestyle',
  sports_news: 'Sports News',
  good_news: 'Good News',
  research_says: 'Research Says',
  bad_recipes: 'Bad Recipes',
  daily_record: 'Daily Record',
  health_wellness: 'Υγεία & Ευεξία',
  personal_finance: 'Προσωπικά Οικονομικά',
  radio_contest: 'Radio Contest',
  one_word_game: 'One Word Game',
  male_vs_female: 'Male vs Female',
  truth_or_myth: 'Αλήθεια ή Μύθος',
  yes_or_no: 'Yes or No',
  real_or_epic_lie: 'Real or Epic Lie',
  man_says_woman_responds: 'Άντρας λέει, Γυναίκα απαντά',
  what_drives_me_crazy: 'What Drives Me Crazy',
  debate_of_the_day: 'Debate of the Day',
  guess_the_profession: 'Μάντεψε το Επάγγελμα',
  guess_song_from_lyric: 'Μάντεψε το Τραγούδι από στίχο',
  todays_confession: 'Today\'s Confession',
  fastest_wins_contest: 'Fastest Wins Contest',
  greek_habits_never_die: 'Greek Habits Never Die',
  funny_habits: 'Funny Habits',
  would_you_rather: 'Would You Rather',
  games_riddles: 'Παιχνίδια & Γρίφοι',
  tech_innovation: 'Tech & Innovation',
  small_secrets: 'Small Secrets',
  on_this_day_charts: 'On This Day Charts',
  today_in_music_history: 'Today in Music History',
  shazam_chart_greece: 'Shazam Chart Greece',
  radio_top_songs: 'Radio Top Songs',
  youtube_trending_greece: 'YouTube Trending Greece',
};

const ContentQA = () => {
  const [scanning, setScanning] = useState(false);
  const [qaResults, setQaResults] = useState(null);
  const [autoFix, setAutoFix] = useState(true);

  // ─── Bulk "check & fill missing sections" (v2.9.7) ────────────────
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const runCheckAndFillMissing = async () => {
    setBulkRunning(true);
    setBulkResult(null);
    try {
      const token = storage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/check-and-fill-missing-sections`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          // Worst case: many missing sections × ~30s each. Allow 30 min.
          timeout: 1800000,
        }
      );
      setBulkResult(res.data);
      const missingCount = (res.data?.missing_or_empty_before || []).length;
      const successCount = (res.data?.regenerated_success || []).length;
      const failedCount = (res.data?.regenerated_failed || []).length;
      if (missingCount === 0) {
        toast.success(
          `✅ Όλες οι 46 ενότητες είναι ήδη πλήρεις — δεν χρειάστηκε καμία ενέργεια.`
        );
      } else if (failedCount === 0) {
        toast.success(
          `✅ Δημιουργήθηκαν ${successCount}/${missingCount} ελλιπείς ενότητες!`
        );
      } else {
        toast.warning(
          `⚠️ Δημιουργήθηκαν ${successCount}/${missingCount} — ${failedCount} απέτυχαν, δες λεπτομέρειες.`
        );
      }
    } catch (err) {
      console.error('Check-and-fill error:', err);
      const detail = err.response?.data?.detail || err.message || 'Άγνωστο σφάλμα';
      setBulkResult({ status: 'error', detail });
      toast.error(`❌ Αποτυχία: ${typeof detail === 'string' ? detail : 'σφάλμα server'}`);
    } finally {
      setBulkRunning(false);
    }
  };

  const runQAScan = async () => {
    setScanning(true);
    try {
      const token = storage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/qa/scan?auto_fix=${autoFix}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setQaResults(response.data.qa_results);
      
      const { status, summary } = response.data.qa_results;
      
      if (status === 'passed') {
        toast.success('✅ Όλοι οι έλεγχοι πέρασαν επιτυχώς!');
      } else if (status === 'fixed') {
        toast.success(`✅ Βρέθηκαν ${summary.total_issues} προβλήματα και διορθώθηκαν ${summary.total_fixes}!`);
      } else {
        toast.warning(`⚠️ Βρέθηκαν ${summary.total_issues} προβλήματα που χρειάζονται έλεγχο`);
      }
      
    } catch (error) {
      console.error('QA Scan error:', error);
      toast.error(error.response?.data?.detail || 'Σφάλμα κατά τον έλεγχο ποιότητας');
    } finally {
      setScanning(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const getIssueIcon = (type) => {
    switch (type) {
      case 'empty_section': return <AlertCircle className="w-4 h-4" />;
      case 'duplicate_content': return <FileCheck className="w-4 h-4" />;
      case 'outdated_date': return <Calendar className="w-4 h-4" />;
      case 'grammar_error': return <Languages className="w-4 h-4" />;
      case 'readability': return <Settings className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileCheck className="w-6 h-6 text-purple-600" />
            Έλεγχος Ποιότητας Περιεχομένου (QA)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Αυτό το εργαλείο ελέγχει αυτόματα το περιεχόμενο για:
            </p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
              <li className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-500" />
                Κενές ενότητες
              </li>
              <li className="flex items-center gap-2">
                <FileCheck className="w-4 h-4 text-blue-500" />
                Επαναλήψεις περιεχομένου
              </li>
              <li className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-green-500" />
                Παλιές ημερομηνίες
              </li>
              <li className="flex items-center gap-2">
                <Languages className="w-4 h-4 text-purple-500" />
                Ελληνική γραμματική
              </li>
              <li className="flex items-center gap-2">
                <Settings className="w-4 h-4 text-orange-500" />
                Κατανοησιμότητα κειμένου
              </li>
              <li className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-500" />
                Αυτόματη διόρθωση
              </li>
            </ul>

            <div className="flex items-center gap-4 pt-4 border-t">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoFix}
                  onChange={(e) => setAutoFix(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium">
                  Αυτόματη διόρθωση προβλημάτων
                </span>
              </label>

              <Button
                onClick={runQAScan}
                disabled={scanning}
                className="ml-auto bg-purple-600 hover:bg-purple-700"
              >
                {scanning ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Έλεγχος σε εξέλιξη...
                  </>
                ) : (
                  <>
                    <FileCheck className="w-4 h-4 mr-2" />
                    Εκτέλεση Ελέγχου QA
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ─── Bulk Check & Fill missing sections (v2.9.7) ─────────────── */}
      <Card data-testid="bulk-check-fill-card" className="border-purple-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wand2 className="w-6 h-6 text-purple-600" />
            Έλεγχος & Δημιουργία Ελλιπών Ενοτήτων
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Σαρώνει και τις <strong>46 ενότητες</strong> της σημερινής ημέρας
              και αυτόματα δημιουργεί ξανά όσες <em>λείπουν ή είναι κενές</em>
              (περιεχόμενο &lt; 50 χαρακτήρες). Χρήσιμο αν το 22:00 cron απέτυχε
              σε κάποιες ενότητες — π.χ. <em>«Για γυναίκες μόνο»</em>.
              <br />
              <span className="text-xs text-gray-500">
                Διαρκεί από 1s (όλα πλήρη) έως ~25 λεπτά (αν λείπουν πολλές).
              </span>
            </p>

            <Button
              onClick={runCheckAndFillMissing}
              disabled={bulkRunning}
              data-testid="bulk-check-fill-btn"
              className="w-full md:w-auto bg-purple-600 hover:bg-purple-700"
            >
              {bulkRunning ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Έλεγχος & Δημιουργία σε εξέλιξη...
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4 mr-2" />
                  Έλεγχος Όλων των Ενοτήτων
                </>
              )}
            </Button>

            {bulkResult && bulkResult.status === 'success' && (
              <div
                className="p-4 rounded-md border space-y-3 text-sm"
                data-testid="bulk-check-fill-result"
              >
                {/* Summary grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-700">
                      {bulkResult.total_expected}
                    </div>
                    <div className="text-xs text-gray-500">Σύνολο ενοτήτων</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {bulkResult.already_present_count}
                    </div>
                    <div className="text-xs text-gray-500">Ήδη πλήρεις</div>
                  </div>
                  <div className="text-center">
                    <div
                      className={`text-2xl font-bold ${
                        (bulkResult.regenerated_success || []).length > 0
                          ? 'text-purple-600'
                          : 'text-gray-400'
                      }`}
                    >
                      {(bulkResult.regenerated_success || []).length}
                    </div>
                    <div className="text-xs text-gray-500">Δημιουργήθηκαν</div>
                  </div>
                  <div className="text-center">
                    <div
                      className={`text-2xl font-bold ${
                        (bulkResult.regenerated_failed || []).length > 0
                          ? 'text-red-600'
                          : 'text-gray-400'
                      }`}
                    >
                      {(bulkResult.regenerated_failed || []).length}
                    </div>
                    <div className="text-xs text-gray-500">Απέτυχαν</div>
                  </div>
                </div>

                <div className="text-xs text-gray-500 text-center pt-2 border-t">
                  Ημερομηνία: <strong>{bulkResult.date}</strong> · Διάρκεια:{' '}
                  <strong>{bulkResult.duration_seconds}s</strong>
                </div>

                {/* Successful list */}
                {(bulkResult.regenerated_success || []).length > 0 && (
                  <div
                    className="bg-green-50 border border-green-200 rounded-md p-3"
                    data-testid="bulk-success-list"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="font-medium text-green-800">
                        Δημιουργήθηκαν επιτυχώς ({bulkResult.regenerated_success.length})
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {bulkResult.regenerated_success.map((key) => (
                        <Badge
                          key={key}
                          className="bg-green-500 hover:bg-green-600 text-white"
                        >
                          {SECTION_LABELS[key] || key}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Failed list */}
                {(bulkResult.regenerated_failed || []).length > 0 && (
                  <div
                    className="bg-red-50 border border-red-200 rounded-md p-3"
                    data-testid="bulk-failed-list"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-4 h-4 text-red-600" />
                      <span className="font-medium text-red-800">
                        Απέτυχαν ({bulkResult.regenerated_failed.length})
                      </span>
                    </div>
                    <ul className="space-y-1">
                      {bulkResult.regenerated_failed.map((f, idx) => (
                        <li key={idx} className="text-xs text-red-700">
                          <strong>{SECTION_LABELS[f.section] || f.section}:</strong>{' '}
                          {f.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* All complete message */}
                {(bulkResult.missing_or_empty_before || []).length === 0 && (
                  <div
                    className="bg-green-50 border border-green-200 rounded-md p-3 flex items-center gap-2"
                    data-testid="bulk-all-complete"
                  >
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-green-800 font-medium">
                      Όλες οι 46 ενότητες είναι ήδη πλήρεις — καμία ενέργεια δεν χρειάστηκε.
                    </span>
                  </div>
                )}
              </div>
            )}

            {bulkResult && bulkResult.status === 'error' && (
              <div
                className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm"
                data-testid="bulk-check-fill-error"
              >
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-medium text-red-800">Αποτυχία ελέγχου</div>
                  <div className="text-red-700 text-xs mt-1">
                    {typeof bulkResult.detail === 'string' ? bulkResult.detail : 'Άγνωστο σφάλμα'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {qaResults && (
        <div className="space-y-4">
          {/* Summary Card */}
          <Card className={`border-2 ${
            qaResults.status === 'passed' ? 'border-green-500' : 
            qaResults.status === 'fixed' ? 'border-yellow-500' : 
            'border-red-500'
          }`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {qaResults.status === 'passed' ? (
                  <>
                    <CheckCircle className="w-6 h-6 text-green-500" />
                    <span className="text-green-700">Όλοι οι έλεγχοι πέρασαν!</span>
                  </>
                ) : qaResults.status === 'fixed' ? (
                  <>
                    <Zap className="w-6 h-6 text-yellow-500" />
                    <span className="text-yellow-700">Προβλήματα διορθώθηκαν</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-6 h-6 text-red-500" />
                    <span className="text-red-700">Βρέθηκαν προβλήματα</span>
                  </>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-700">
                    {qaResults.summary.total_sections}
                  </div>
                  <div className="text-sm text-gray-500">Σύνολο ενοτήτων</div>
                </div>
                <div className="text-center">
                  <div className={`text-3xl font-bold ${
                    qaResults.summary.total_issues === 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {qaResults.summary.total_issues}
                  </div>
                  <div className="text-sm text-gray-500">Προβλήματα</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-600">
                    {qaResults.summary.total_fixes}
                  </div>
                  <div className="text-sm text-gray-500">Διορθώσεις</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">
                    {qaResults.summary.empty_sections}
                  </div>
                  <div className="text-sm text-gray-500">Κενές ενότητες</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Issues List */}
          {qaResults.issues && qaResults.issues.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Προβλήματα που βρέθηκαν ({qaResults.issues.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {qaResults.issues.map((issue, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border"
                    >
                      <div className="mt-1">
                        {getIssueIcon(issue.type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge className={getSeverityColor(issue.severity)}>
                            {issue.severity === 'high' ? 'Υψηλό' : 
                             issue.severity === 'medium' ? 'Μέτριο' : 'Χαμηλό'}
                          </Badge>
                          <span className="text-xs text-gray-500">
                            {issue.section}
                          </span>
                        </div>
                        <p className="text-sm mt-1">{issue.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Fixes Applied */}
          {qaResults.fixes_applied && qaResults.fixes_applied.length > 0 && (
            <Card className="border-green-200 bg-green-50">
              <CardHeader>
                <CardTitle className="text-green-700">
                  Διορθώσεις που εφαρμόστηκαν ({qaResults.fixes_applied.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {qaResults.fixes_applied.map((fix, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-3 bg-white rounded-lg border border-green-200"
                    >
                      <CheckCircle className="w-4 h-4 text-green-500 mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-green-500">
                            {fix.type}
                          </Badge>
                          <span className="text-xs text-gray-500">
                            {fix.section}
                          </span>
                        </div>
                        <p className="text-sm mt-1">{fix.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default ContentQA;
